import Button, { ButtonType } from "components/Elements/Buttonv2";
import Input from "components/Elements/Inputv2";
import searchIconImage from "./svg/search-icon.svg";
import threeDotsIconImage from "./svg/three-dots-icon.svg";
import React, { Fragment, useEffect, useState } from "react";
import Table from "components/Tablev2";
import { format } from "date-fns";
import { Menu, Transition } from "@headlessui/react";
import ApiService from "services/api.service";
import { useNavigate } from "react-router-dom";
import Pagination from "components/Pagination";
import { toast } from "react-toastify";
import { useDebounce } from "react-use";
import {
  keepPreviousData,
  QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import sortAscChevronsImage from "./svg/sort-asc-chevrons.svg";
import sortDescChevronsImage from "./svg/sort-desc-chevrons.svg";
import sortNoneChevronsImage from "./svg/sort-none-chevrons.svg";
import emptyDataImage from "./svg/empty-data.svg";
import NameGroupModal from "./Modals/NameGroupModal";
import { Group, GroupType } from "types/Group";
import { Link } from "react-router-dom";
import Select from "components/Elements/Selectv2";

enum GroupStatus {
  UPDATING = "Updating",
  UPDATED = "Updated",
}

interface GroupRowData {
  id: string;
  name: string;
  type: string;
  status: GroupStatus;
  lastUpdate: string;
}

enum SortProperty {
  UPDATED_AT = "updatedAt",
}

enum SortType {
  ASC = "asc",
  DESC = "desc",
}

interface SortOptions {
  sortBy: SortProperty;
  sortType: SortType;
}

export const isUpdatingClassName: Record<GroupStatus, string> = {
  [GroupStatus.UPDATED]: "bg-[#DCFCE7] text-[#14532D]",
  [GroupStatus.UPDATING]: "bg-[#FEF9C3] text-[#713F12]",
};

const GROUPS_ITEMS_PER_PAGE = 15;

export interface GroupsQueryParams {
  page: number;
  search: string;
  sortBy: SortProperty;
  sortType: SortType;
}

export const DEFAULT_GROUPS_QUERY_PARAMS: GroupsQueryParams = {
  page: 1,
  search: "",
  sortBy: SortProperty.UPDATED_AT,
  sortType: SortType.DESC,
};

// Single source of truth for the groups list request — used by both the
// page and the app-entry prefetch so they resolve to the same cache entry.
export const buildGroupsQuery = (params: GroupsQueryParams) => ({
  queryKey: [
    "groups",
    {
      page: params.page,
      search: params.search,
      sortBy: params.sortBy,
      sortType: params.sortType,
    },
  ],
  queryFn: async () => {
    const {
      data: { data: results, totalPages },
    } = await ApiService.get<{
      data: Group[];
      totalPages: number;
    }>({
      url: `/groups?take=${GROUPS_ITEMS_PER_PAGE}&skip=${
        (params.page - 1) * GROUPS_ITEMS_PER_PAGE
      }&search=${params.search}&orderBy=${params.sortBy}&orderType=${
        params.sortType
      }`,
    });

    const rows: GroupRowData[] = results.map((group) => ({
      id: group.id,
      name: group.name,
      type: group.type,
      lastUpdate: group.updatedAt || group.createdAt || "",
      status: group.isUpdating ? GroupStatus.UPDATING : GroupStatus.UPDATED,
    }));

    return { rows, totalPages };
  },
});

export const prefetchGroups = (queryClient: QueryClient) =>
  queryClient.prefetchQuery(buildGroupsQuery(DEFAULT_GROUPS_QUERY_PARAMS));

// A single group's detail and member count. Cached per id so opening a group is
// instant once warmed (on hover from the list, or after a previous visit).
export const buildGroupDetailQuery = (id: string) => ({
  queryKey: ["group-detail", id],
  queryFn: async () => {
    const { data } = await ApiService.get({ url: `/groups/${id}` });
    return data;
  },
});

export const buildGroupContactsCountQuery = (id: string) => ({
  queryKey: ["group-contacts-count", id],
  queryFn: async () => {
    const { data } = await ApiService.get<number>({
      url: `/groups/${id}/contacts/count`,
    });
    return data;
  },
});

// Warm the cache for a group before the user opens it (called on row hover).
export const prefetchGroupDetail = (queryClient: QueryClient, id: string) => {
  queryClient.prefetchQuery(buildGroupDetailQuery(id));
  queryClient.prefetchQuery(buildGroupContactsCountQuery(id));
};

const GroupTablev2 = () => {
  const navigate = useNavigate();

  const queryClient = useQueryClient();

  const [isNameGroupModalOpen, setIsNameGroupModalOpen] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sortOptions, setSortOptions] = useState<SortOptions>({
    sortBy: SortProperty.UPDATED_AT,
    sortType: SortType.DESC,
  });

  const [currentPage, setCurrentPage] = useState(1);

  // Debounce the search box into the value the query uses so typing doesn't
  // fire a request per keystroke.
  useDebounce(() => setDebouncedSearch(search), 500, [search]);

  const { data, isPending, isError, isSuccess } = useQuery({
    // Shared factory so the page and the app-entry prefetch hit the same key.
    ...buildGroupsQuery({
      page: currentPage,
      search: debouncedSearch,
      sortBy: sortOptions.sortBy,
      sortType: sortOptions.sortType,
    }),
    // Keep the current rows on screen while the next request runs, so paging,
    // filtering, and returning to the page never blank the table.
    placeholderData: keepPreviousData,
  });

  const rows = data?.rows ?? [];
  const pagesCount = data?.totalPages ?? 1;

  useEffect(() => {
    if (isError) toast.error("Failed to load data");
  }, [isError]);

  useEffect(() => {
    setCurrentPage(1);
  }, [sortOptions, debouncedSearch]);

  useEffect(() => {
    setSearch("");
  }, [showSearch]);

  // Other parts of the app dispatch this when they change groups.
  useEffect(() => {
    const onChange = () => {
      queryClient.invalidateQueries({ queryKey: ["groups"] });
    };
    window.addEventListener("leadstats:groups-changed", onChange);
    return () =>
      window.removeEventListener("leadstats:groups-changed", onChange);
  }, [queryClient]);

  type GroupsCache = { rows: GroupRowData[]; totalPages: number };

  // Duplicate a group and insert the returned copy at the top of the current
  // page instead of refetching the list.
  const duplicateMutation = useMutation({
    mutationFn: (id: string) =>
      ApiService.post<Group>({ url: `/groups/${id}/duplicate` }),
    onSuccess: ({ data: seg }) => {
      const row: GroupRowData = {
        id: seg.id,
        name: seg.name,
        type: seg.type,
        lastUpdate: new Date().toUTCString(),
        status: seg.isUpdating ? GroupStatus.UPDATING : GroupStatus.UPDATED,
      };
      const { queryKey } = buildGroupsQuery({
        page: currentPage,
        search: debouncedSearch,
        sortBy: sortOptions.sortBy,
        sortType: sortOptions.sortType,
      });
      queryClient.setQueryData<GroupsCache>(queryKey, (old) =>
        old
          ? {
              ...old,
              rows: [row, ...old.rows].slice(0, GROUPS_ITEMS_PER_PAGE),
            }
          : old
      );
    },
  });

  const handleDuplicateGroup = (id: string) =>
    toast.promise(duplicateMutation.mutateAsync(id), {
      pending: "Duplicating group...",
      success: "Group duplicated",
      error: "Failed to duplicate group",
    });

  // Delete a group optimistically: drop the row from every cached page, roll
  // back on failure, no list refetch.
  const deleteMutation = useMutation({
    mutationFn: (id: string) => ApiService.delete({ url: "/groups/" + id }),
    onMutate: async (id: string) => {
      await queryClient.cancelQueries({ queryKey: ["groups"] });
      const snapshot = queryClient.getQueriesData<GroupsCache>({
        queryKey: ["groups"],
      });
      queryClient.setQueriesData<GroupsCache>({ queryKey: ["groups"] }, (old) =>
        old ? { ...old, rows: old.rows.filter((r) => r.id !== id) } : old
      );
      return { snapshot };
    },
    onError: (_err, _id, context) => {
      context?.snapshot.forEach(([key, value]) =>
        queryClient.setQueryData(key, value)
      );
      toast.error("Failed to delete group");
    },
  });

  const handleDeleteGroup = (id: string) => {
    if (!window.confirm("Delete this group? This cannot be undone.")) return;
    deleteMutation.mutate(id);
  };

  return (
    <div className="p-5 flex flex-col gap-5 font-inter font-normal text-[14px] text-themed-text leading-[22px]">
      <div className="flex justify-between items-center">
        <div className="text-[20px] font-semibold leading-[28px]">Groups</div>

        <div>
          <Select
            options={[
              { key: GroupType.MANUAL, title: "Upload CSV" },
              { key: GroupType.AUTOMATIC, title: "Dynamic rules" },
            ]}
            value=""
            id="createGroupSelect"
            onChange={(value) => {
              if (value === GroupType.MANUAL) navigate("/group/create/manual");

              if (value === GroupType.AUTOMATIC)
                navigate("/group/create/automatic");
            }}
            placeholder="Create Group"
            customBTN={
              <Button type={ButtonType.PRIMARY} onClick={() => {}}>
                Create Group
              </Button>
            }
            panelClassName="!max-w-[130px]"
          />
        </div>
      </div>
      <div className="p-5 bg-themed-card rounded-lg flex flex-col gap-5">
        {rows.length === 0 && search === "" && isSuccess ? (
          <div className="w-full h-[300px] flex items-center justify-center select-none">
            <div className="flex flex-col items-center gap-5">
              <img src={emptyDataImage} />

              <div className="font-inter text-[16px] font-semibold leading-[24px] text-themed-textSecondary">
                Create a group
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="flex justify-end items-center">
              {showSearch ? (
                <div className="flex gap-[10px] items-center">
                  <Input
                    id="search-input"
                    value={search}
                    onChange={setSearch}
                    placeholder="Search all groups"
                    showClearButton
                  />

                  <Button
                    type={ButtonType.LINK}
                    onClick={() => setShowSearch(false)}
                  >
                    Cancel
                  </Button>
                </div>
              ) : (
                <button onClick={() => setShowSearch(true)} id="show-search">
                  <img src={searchIconImage} />
                </button>
              )}
            </div>

            <Table
              isLoading={isPending}
              headings={[
                <div className="px-5 py-[10px] select-none">Name</div>,
                <div className="px-5 py-[10px] select-none">Type</div>,
                <div
                  className="px-5 py-[10px] select-none flex gap-[2px] items-center cursor-pointer"
                  onClick={() => {
                    if (sortOptions.sortBy !== SortProperty.UPDATED_AT) {
                      setSortOptions({
                        sortBy: SortProperty.UPDATED_AT,
                        sortType: SortType.DESC,
                      });

                      return;
                    }

                    if (sortOptions.sortType === SortType.ASC) {
                      setSortOptions({
                        sortBy: SortProperty.UPDATED_AT,
                        sortType: SortType.DESC,
                      });

                      return;
                    }

                    setSortOptions({
                      sortBy: SortProperty.UPDATED_AT,
                      sortType: SortType.ASC,
                    });
                  }}
                >
                  <div>Last update</div>
                  <div>
                    <img
                      src={
                        sortOptions.sortBy === SortProperty.UPDATED_AT
                          ? sortOptions.sortType === SortType.ASC
                            ? sortAscChevronsImage
                            : sortDescChevronsImage
                          : sortNoneChevronsImage
                      }
                    />
                  </div>
                </div>,
                <div className="px-5 py-[10px] select-none">Status</div>,
                ,
                <div className="px-5 py-[10px] select-none"></div>,
              ]}
              rowsData={rows}
              rows={rows.map((row) => [
                <button
                  className="text-[#6366F1]"
                  onMouseEnter={() => prefetchGroupDetail(queryClient, row.id)}
                  onFocus={() => prefetchGroupDetail(queryClient, row.id)}
                  onClick={() => navigate(`/group/${row.id}`)}
                >
                  {row.name}
                </button>,
                <div>{row.type}</div>,
                <div>
                  {row.lastUpdate
                    ? format(new Date(row.lastUpdate), "MM/dd/yyyy HH:mm")
                    : "-"}
                </div>,
                <div
                  className={`px-[10px] py-[2px] rounded-[14px] w-fit ${
                    isUpdatingClassName[row.status]
                  }`}
                >
                  {row.status}
                </div>,
                <Menu as="div" className="relative">
                  <Menu.Button>
                    <button className="px-[5px] py-[11px] rounded">
                      <img
                        className="row-actions-icon"
                        src={threeDotsIconImage}
                      />
                    </button>
                  </Menu.Button>
                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                  >
                    <Menu.Items className="absolute z-[120] right-0 origin-top-right w-[200px] py-[4px] rounded-sm bg-themed-card shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                      <Menu.Item>
                        {({ active }) => (
                          <button
                            className={`block w-full text-left py-[5px] px-[12px] ${
                              active ? "bg-themed-background" : ""
                            }`}
                            onClick={() => handleDuplicateGroup(row.id)}
                          >
                            Duplicate
                          </button>
                        )}
                      </Menu.Item>
                      <Menu.Item>
                        {({ active }) => (
                          <button
                            className={`block w-full text-left py-[5px] px-[12px] text-[#F43F5E] ${
                              active ? "bg-themed-background" : ""
                            }`}
                            onClick={() => handleDeleteGroup(row.id)}
                          >
                            Delete
                          </button>
                        )}
                      </Menu.Item>
                    </Menu.Items>
                  </Transition>
                </Menu>,
              ])}
            />
          </>
        )}

        {pagesCount > 1 && (
          <div className="flex justify-center items-center">
            <Pagination
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              totalPages={pagesCount}
            />
          </div>
        )}
      </div>

      <NameGroupModal
        isOpen={isNameGroupModalOpen}
        onClose={() => setIsNameGroupModalOpen(false)}
      />
    </div>
  );
};

export default GroupTablev2;
